/**
 * How long each kind of record is kept (CLAUDE.md "Scale").
 *
 * WHY THIS EXISTS. An audit log with no end grows forever: it is the table
 * that gets big first, and the one nobody thinks about until a backup takes an
 * hour. Every log here says how long it is worth keeping and why, in one place,
 * so the answer is a decision rather than an accident.
 *
 * Records people create are NOT here. Those are deleted by people, recoverably
 * (docs/SECURITY.md); nothing expires a customer's own data behind their back.
 */
export interface RetentionRule {
  /** The table, as the database names it. */
  table: string
  /** The timestamp column the age is measured from. */
  column: string
  /** Days to keep. */
  days: number
  /** Why this long — the part worth reading in a year. */
  why: string
}

const YEAR = 365

export const retentionRules: RetentionRule[] = [
  {
    table: 'record_change',
    column: 'at',
    days: YEAR + 35,
    why: 'The activity log: a year plus a month, so "what happened this time last year" is still answerable.',
  },
  {
    table: 'impersonation_log',
    column: 'started_at',
    days: YEAR + 35,
    why: 'Kept as long as the activity log it explains.',
  },
  {
    table: 'webhook_delivery',
    column: 'received_at',
    days: 90,
    why: 'Each delivery keeps the message as sent, which may hold personal details. Ninety days is enough to see what a sender did, reprocess a failure and check a fix; past that it is only risk. The counts stay in the activity log where a change was made.',
  },
  {
    table: 'webhook_alert_flag',
    column: 'flagged_at',
    days: 2 * YEAR,
    why: 'The no-data alert’s flag history: a few rows a month per webhook. Two years answers "how often has this feed gone quiet" across seasons; older flags explain nothing new.',
  },
  {
    table: 'email_change',
    column: 'created_at',
    days: YEAR,
    why: 'Long enough to answer "who changed my email", short enough not to hold old addresses forever.',
  },
  {
    table: 'invitation_link',
    column: 'created_at',
    days: YEAR,
    why: 'Says which address an invitation link was used with, long enough to answer "how did they get in"; the activity log keeps the join itself.',
  },
  {
    table: 'pending_signup',
    column: 'expires_at',
    days: 1,
    why: 'A changed address waiting to be confirmed holds a password hash; once its hour is up it is useless, so it goes a day later.',
  },
  {
    table: 'assistant_conversation',
    column: 'updated_at',
    days: 180,
    why: 'Chats are a working aid, not a record. Their messages go with them; what the assistant CHANGED stays in the activity log.',
  },
  {
    table: 'mcp_call',
    column: 'at',
    days: YEAR,
    why: 'The MCP usage log (docs/MCP.md): every call an AI app made — its arguments verbatim — by whom, and what it pulled. A year answers "who pulled that, and when"; a project that wants it as a permanent record moves it to keptForever and says so in docs/DECISIONS.md.',
  },
  {
    table: 'oauth_access_token',
    column: 'expires_at',
    days: 1,
    why: 'An AI app gets a new one-hour access token every hour it is used (ADR mcp-server). An expired one is useless; it goes a day later.',
  },
  {
    table: 'oauth_refresh_token',
    column: 'expires_at',
    days: 1,
    why: 'Refresh tokens rotate and last 30 days; an expired one can never be used again.',
  },
  {
    table: 'service_usage',
    column: 'updated_at',
    days: 2 * YEAR,
    why: 'Two years of monthly usage is enough to see a trend and compare a year on year.',
  },
  {
    table: 'monday_import_row',
    column: 'created_at',
    days: 1,
    why: 'Staging for one Monday import: each parsed row, cell values included. The import deletes them when the run is committed or discarded; anything left after a day belongs to a run nobody finished, and is only risk (DATA-MODEL-V1 §2.10).',
  },
]

/**
 * Majik's records (docs/majik/DATA-MODEL-V1.md §2): people create and delete
 * them themselves, recoverably — nothing expires a customer's own data behind
 * their back. A child row goes with its parent.
 */
const MAJIK_RECORD =
  'Majik’s own records, created and deleted by people (recoverably, docs/SECURITY.md) — never expired behind their back'
const majikRecords = Object.fromEntries(
  [
    'contacts',
    'clients',
    'locations',
    'client_contacts',
    'cleaners',
    'cleaner_time_off',
    'work_types',
    'recurring_jobs',
    'jobs',
    'products',
    'invoices',
    'orders',
    'inspections',
  ].map((table) => [table, MAJIK_RECORD]),
)
const MAJIK_CHILD = 'part of its parent record — kept or removed with it'
const majikChildren = Object.fromEntries(
  [
    'contact_methods',
    'team',
    'team_member',
    'team_alert',
    'billing_items',
    'recurring_job_cleaners',
    'job_cleaners',
    'job_work_types',
    'invoice_lines',
    'order_lines',
    'inspection_locations',
    'communication_links',
  ].map((table) => [table, MAJIK_CHILD]),
)

/** Tables deliberately kept forever, each with its reason. */
export const keptForever: Record<string, string> = {
  app_setting: 'a handful of settings an admin changed, like the assistant’s monthly cap',
  record_history:
    'every real change to every main record, from every source (docs/DATA-MODEL.md), kept as long as the record — Delete forever erases it with the record. What each record was, which nothing else can answer. Only real changes are written: saving something unchanged, or a feed re-sending it, adds nothing. Revisit if it passes a few GB (docs/DATA-MODEL.md → "Retention")',
  data_field_override:
    'a current-value table — at most one row per field, overwritten in place by each edit; the activity log keeps every before and after',
  data_code_override:
    'a current-value table — at most one row per described code, capped at MAX_CODE_OVERRIDES; overwritten in place, the activity log keeps every before and after',
  auto_join_domain:
    'a handful of trusted domains an Admin listed; they are the setting itself, removed only when an Admin removes them',
  mcp_token:
    'one row per personal access token anyone made (docs/MCP.md) — revoked and expired ones are kept, hashed and useless, so the Admin’s list and the usage log can still say whose a token was',
  service_metric_start: 'one row per metric, saying when counting began',
  spend_statement: 'one small row per service per month — the bill history itself',
  deploy_log: 'one row per version ever deployed; tiny, and the change log reads it',
  service_price: 'the prices you set; losing them would silently change old figures',
  service_meter_check: 'one row per meter, overwritten in place',
  webhook_endpoint:
    'a handful of rows, one per webhook; a deleted one waits in Recently deleted until an Admin deletes it forever, which takes its run history with it',
  user_preference:
    'a person’s own settings, like sidebar width; there is nothing to expire, and the row is removed automatically when their account is, via ON DELETE CASCADE',
  ...majikRecords,
  ...majikChildren,
  business_rules:
    'Majik’s business rules — one row, overwritten in place; the activity log keeps every before and after',
  communications:
    'the record of what was said to whom, and when (DATA-MODEL-V1 §2.9) — kept as long as the records it is about; Delete forever erases a note',
  monday_import_run:
    'one row per Monday upload — file names, counts and the review lists, never a cell value. The import service removes previews after 7 days (WP-H); committed runs stay until Monday is switched off, then 90 days more — that date becomes a rule here then',
  monday_record:
    'the Monday crosswalk and last-imported snapshots: what stops a re-import overwriting an app edit. Kept while Monday is still exported; removed with the committed runs when Monday is switched off (+ 90 days)',
  monday_reference_map:
    'an Admin’s answers to “which record does this Monday name mean?”, reused by every later import; removed with the crosswalk',
}
